import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Skeleton,
  useTheme,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderDetailStatus } from '@api/enterprise/openapi/models/order-detail-status';
import { useSidebarEquipment } from '../hooks';

type EquipmentListProps = {
  jobNumber: string;
  currentWoDetailGlobalId?: string;
  collapsed?: boolean;
  onNavigate: (woDetailGlobalId: string) => void;
};

export const EquipmentList: React.FC<EquipmentListProps> = ({
  jobNumber,
  currentWoDetailGlobalId,
  collapsed = false,
  onNavigate
}) => {
  const theme = useTheme();
  const { t } = useTranslation('sidebar');
  const { equipment, isLoading, error } = useSidebarEquipment(jobNumber);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="error">
          {t('equipment.loadError')}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <List sx={{ py: 0 }}>
        {[1, 2, 3].map(i => (
          <ListItem key={i} disablePadding>
            <Box
              sx={{
                width: '100%',
                p: 1.5,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Skeleton
                variant="circular"
                width={12}
                height={12}
                sx={{ mr: 1.5 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  }

  if (equipment.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('equipment.noEquipment')}
        </Typography>
      </Box>
    );
  }

  // Don't show anything when collapsed
  if (collapsed) {
    return null;
  }

  return (
    <List sx={{ py: 0, maxHeight: '40vh', overflow: 'auto' }}>
      {equipment.map(eq => {
        const isActive = currentWoDetailGlobalId === eq.globalId;

        return (
          <ListItem key={eq.globalId || eq.sequenceNumber} disablePadding>
            <ListItemButton
              onClick={() => onNavigate(eq.globalId)}
              selected={isActive}
              sx={{
                px: 2,
                py: 1.2,
                backgroundColor: isActive
                  ? theme.palette.mode === 'light'
                    ? 'rgba(1, 103, 124, 0.08)'
                    : 'rgba(68, 156, 180, 0.16)'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.mode === 'light'
                      ? 'rgba(1, 103, 124, 0.12)'
                      : 'rgba(68, 156, 180, 0.24)'
                    : theme.palette.action.hover
                },
                borderLeft: isActive
                  ? `3px solid ${theme.palette.primary.main}`
                  : 'none',
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start'
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isActive ? 600 : 500,
                      color: theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.4,
                      flex: 1
                    }}
                  >
                    {eq.id}
                  </Typography>
                  {eq.status === OrderDetailStatus.Complete && (
                    <Chip
                      label="Done"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.15)'
                            : 'rgba(76, 175, 80, 0.12)',
                        color:
                          theme.palette.mode === 'dark' ? '#66bb6a' : '#4caf50',
                        '& .MuiChip-label': {
                          px: 0.75
                        }
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3
                  }}
                >
                  {eq.description}
                </Typography>
              </Box>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};
